/**
 * feat-107 Slice A — slice-owned key→emoji map for the IconPickerPopover.
 *
 * The frozen service catalogs (`LESSON_ICON_CATALOG` / `UNIT_MASCOT_CATALOG`)
 * store `{ key, label }` only — no display glyph (ADR 0022 D2). The web admin
 * renders emoji swatches (per the mockup), so the call site supplies this
 * `glyphForKey` map. Keeping it here (not in the catalog) keeps the catalog keys
 * the sole cross-app contract. Unknown keys fall back to the catalog label's
 * first letter via the shared component, but we map every frozen key here so the
 * picker and tree/list swatches always show a real glyph.
 */

import {
  LESSON_ICON_CATALOG,
  UNIT_MASCOT_CATALOG,
} from '../../../services/quizContentAdminService';

/** Drill icons (LESSON_ICON_CATALOG, 12 keys) + Lesson mascots (UNIT_MASCOT_CATALOG, 10 keys). */
const GLYPHS: Record<string, string> = {
  // LESSON_ICON_CATALOG
  chat_bubble: '💬',
  microphone: '🎤',
  text_t: '🔤',
  star: '⭐',
  book: '📖',
  utensils: '🍽️',
  lightbulb: '💡',
  trophy: '🏆',
  target: '🎯',
  heart: '❤️',
  wine_glass: '🍷',
  checklist: '✅',
  // UNIT_MASCOT_CATALOG
  elephant: '🐘',
  teacup: '🍵',
  sandwich: '🥪',
  perfect_pairing: '🍶',
  personalized_upsells: '🙋',
  handling_no_thanks: '🙅',
  safety_sanitation: '🧼',
  portion_control: '⚖️',
  daily_special: '🌟',
  respect_community: '🤝',
};

const LABEL_BY_KEY: Record<string, string> = Object.fromEntries(
  [...LESSON_ICON_CATALOG, ...UNIT_MASCOT_CATALOG].map((e) => [e.key, e.label]),
);

/** Maps a catalog key to an emoji glyph; falls back to the label's first letter. */
export function glyphForKey(key: string): string {
  return GLYPHS[key] ?? (LABEL_BY_KEY[key]?.charAt(0) ?? '＋');
}
